import React, { useState, useEffect } from 'react';
import ProjectWiseTab from './ProjectWiseTab';
import axiosInstance from '../../services/api/axios';
import { MessageCircle, Search, ChevronRight, ChevronLeft, Building, Loader, AlertTriangle } from 'lucide-react';
import { useUser } from '../../context/UserContext';

function Communications() {
  const [activeSocialTab, setActiveSocialTab] = useState('whatsapp');
  const [activeProject, setActiveProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategoryTab, setActiveCategoryTab] = useState('leads');
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { userInfo } = useUser();

  // Check if Meta secrets are missing (for DEVELOPER role)
  const metaInfo = userInfo?.loginIdentifierDetails?.customer;

  // Helper to check if a project has Meta configured
  const isProjectMetaConfigured = (project) => !!project?.socialMediaConfiguration?.whatsapp?.metaId;

  const socialTabs = [
    { id: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle size={18} /> },
    { id: 'instagarm', label: 'Instagram', icon: <MessageCircle size={18} /> },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/projects', {
          params: { customerId: metaInfo?.customerId }
        });
        
        // Handle both paginated and non-paginated responses
        const projectsArray = response?.items || (Array.isArray(response) ? response : []);

        setProjects(projectsArray);
        if (projectsArray.length > 0) {
          setActiveProject(projectsArray[0]);
        } else {
          setActiveProject(null);
        }
      } catch (err) {
        setError(err);
        console.error("Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleSocialTabClick = (tabId) => {
    setActiveSocialTab(tabId);
  };

  const handleTabClick = (tabId) => {
    setActiveCategoryTab(tabId);
  };

  const handleProjectClick = (project) => {
    setActiveProject(project);
  };

  const toggleProjects = () => {
    setProjectsOpen(!projectsOpen);
  };

  // Filter projects based on search term
  const filteredProjects = projects.filter(project => 
    project.projectName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center">
          <Loader className="w-10 h-10 text-flamingo-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="bg-white p-6 rounded-lg shadow-md border border-red-200 max-w-md">
          <h3 className="text-red-600 font-medium text-lg mb-2">Error Loading Projects</h3>
          <p className="text-gray-700">{error.message || "An unexpected error occurred"}</p>
          <button 
            className="mt-4 bg-flamingo-500 text-white px-4 py-2 rounded-md hover:bg-flamingo-700 transition"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const sidebarWidth = projectsOpen ? 'w-72' : 'w-14';
  const mainContentWidth = projectsOpen ? 'flex-1' : 'flex-5';

  return (
    <div className="flex flex-col h-screen ">
      {/* Banner for Meta setup */}
      {/* Remove global isMetaNotConfigured, use per-project check */}

      {/* Header */}
      <div className=" border-b border-gray-200 px-6 py-4">
        <h1 className="text-xl font-semibold text-gray-800 flex items-center">
          {/* <span className="bg-flamingo-500 text-white p-1.5 rounded-md mr-3">
            <MessageCircle size={18} />
          </span> */}
          Sales Agent
        </h1>
      </div>

      {/* Social Tabs */}
      <div className="relative pt-2 pl-2">
        <div className="w-full flex justify-start border-gray-300">
          {socialTabs.map((tab) => (
            <button
              key={tab.id}
              className={`
                py-2 px-16 cursor-pointer text-center transition-all duration-300 ease-in-out relative
                ${
                  activeSocialTab === tab.id
                    ? 'text-flamingo-500 font-semibold'
                    : 'text-gray-600 hover:text-flamingo-500'
                }
              `}
              onClick={() => isProjectMetaConfigured(activeProject) && handleSocialTabClick(tab.id)}
              disabled={!isProjectMetaConfigured(activeProject)}
              style={!isProjectMetaConfigured(activeProject) ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              {tab.label}
              {activeSocialTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-flamingo-500 transform transition-all duration-300 ease-in-out" />
              )}
            </button>
          ))}
        </div>
      </div>
       
    
      <div className="flex flex-1 overflow-hidden relative">
        {/* Sidebar (Projects) */}
        <div className={`bg-white shadow-md flex flex-col transition-all duration-300 ease-in-out overflow-hidden border-r border-gray-200 ${sidebarWidth}`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            {projectsOpen && <h2 className="text-md font-normal text-gray-700">Projects</h2>}
            <button 
              onClick={toggleProjects} 
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
              aria-label={projectsOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {projectsOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </button>
          </div>
          {projectsOpen && (
            <>
              <div className="p-3 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 pl-9 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              <div className="overflow-y-auto flex-1 p-2">
                {filteredProjects.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 text-center">No matching projects found</p>
                ) : (
                  <nav className="flex flex-col gap-1">
                    {filteredProjects.map((project) => {
                      const metaConfigured = isProjectMetaConfigured(project);
                      const isActive = activeProject?.projectId === project.projectId;
                      return (
                        <button
                          key={project.projectId}
                          onClick={() => handleProjectClick(project)}
                          className={`
                            group flex items-center w-full px-3 py-2  transition-colors duration-200
                            ${isActive ? ' bg-slate-50 border-r-4 border-slate-600 text-slate-700' : 'text-gray-600 hover:bg-gray-50'}
                            ${!metaConfigured ? 'opacity-80' : ''}
                            focus:outline-none
                          `}
                          style={{
                            minHeight: 48,
                            justifyContent: 'flex-start',
                          }}
                          aria-current={isActive ? "page" : undefined}
                        >
                          {/* <span className="mr-3 flex-shrink-0">
                            <Building size={22} className={isActive ? "text-blue-600" : "text-gray-400 group-hover:text-blue-500"} />
                          </span> */}
                          <span className="truncate flex-1 text-left text-base">{project.projectName}</span>
                          {!metaConfigured && (
                            <span
                              className="flex items-center flex-shrink-0 ml-2"
                              title="Meta not configured"
                            >
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100">
                                <AlertTriangle size={16} className="text-yellow-500" />
                              </span>
                            </span>
                          )}
                          
                        </button>
                      );
                    })}
                  </nav>
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className={`${mainContentWidth} flex flex-col transition-all duration-300 overflow-hidden relative`}>
          {!isProjectMetaConfigured(activeProject) && (
            <div className="absolute inset-0 bg-white bg-opacity-70 z-20 flex flex-col items-center justify-center">
              <div className="text-lg font-semibold  mb-2">Meta (WhatsApp) is not configured for this project.</div>
              <div className="text-sm  mb-4">Please configure WhatsApp integration for this project to access communications features.</div>
              <button
                className="px-4 py-2 bg-flamingo-500 text-white rounded hover:bg-flamingo-600"
                onClick={() => window.location.href = '/profile#onboarding-whatsapp'}
              >
                Configure Now
              </button>
            </div>
          )}
          {isProjectMetaConfigured(activeProject) && (
            activeProject ? (
              <ProjectWiseTab
                socialMedia={activeSocialTab}
                project={activeProject}
                categoryTab={activeCategoryTab}
                onTabClick={handleTabClick}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-200 max-w-md">
                  <Building size={36} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No Project Selected</h3>
                  <p className="text-gray-500 mb-4">Please select a project from the sidebar to view its communication channels.</p>
                  {!projectsOpen && (
                    <button
                      onClick={toggleProjects}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Show Projects
                    </button>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default Communications;